/**
 * ブラウザのデスクトップ通知（FCM Web Push）TGW-3137
 *
 * アプリに送っているプッシュ通知（サイレント通知を除く）を、ブラウザのデスクトップ通知でも受け取れるようにする。
 *
 * 流れ:
 *   1. サーバが <meta name="tg-web-push"> に Firebase の公開設定と「このブラウザの購読がサーバ側で有効か
 *      （deviceActive）」を入れる（WebPushHelper）。meta が無い（未ログイン・アプリ内 WebView・未設定）ときは何もしない。
 *   2. 利用者が「許可する」を押す（通知設定画面のトグル / メッセージ一覧の案内）→ subscribe():
 *      通知の許可を求め → Service Worker（/firebase-messaging-sw.js）を登録 → FCM トークンを取り
 *      → POST /api/web_push/subscribe で users_devices（os='web'）に保存。
 *   3. 以降のページ表示では autoSync() が、購読済みのブラウザのトークンをサーバへ送り直す。
 *      12 時間に 1 回（FCM のトークンは入れ替わる。前回のトークンも送って同じ行を使い回す）。
 *      ただし deviceActive が false（ログアウトで止めた後の再ログイン、FCM が UNREGISTERED を返して止めた等）
 *      なら時間を待たず再登録する。localStorage の「購読済み」はサーバの状態より弱い。
 *      別のユーザーでログインしたブラウザ（userId 不一致）では、前のユーザーの購読を止めて localStorage を消す。
 *      そのユーザーが欲しければ自分で許可してもらう（共用PCで前の人の DM が出続けるのを防ぐ）。
 *   4. ページが裏にあるときの通知表示は Firebase SDK（Service Worker）が行う。
 *      ページが手前にあるときは SDK は何も出さないので、ここで showNotification する（その部屋を開いているときは出さない）。
 *
 * Firebase SDK は npm ではなく gstatic の compat 版を必要になったときだけ <script> で読む（版は meta.sdkVersion）。
 * 購読していないブラウザでは SDK を読まない。購読済みでもページの描画が終わって手が空いてから読む。
 *
 * 公開 API（React からは import、外からは window.TunaGateWebPush）:
 *   status()           … { available, supported, permission, enabled }
 *   subscribe()        … 許可を求めて購読。状態の変化は onChange で届く
 *   unsubscribe()      … 購読解除
 *   shouldShowBanner(status?) … メッセージ一覧の案内を出すべきか
 *   dismissBanner()    … 案内を 30 日間出さない
 *   onChange(cb)       … 状態が変わったら cb(status()) を呼ぶ。戻り値は解除関数
 */
import axios from '../components/axios';

const META_NAME = 'tg-web-push';
const STORAGE_KEY = 'tg_web_push'; // { userId, token, syncedAt }
const DISMISS_KEY = 'tg_web_push_banner_dismissed_at';
const SDK_BASE = 'https://www.gstatic.com/firebasejs/';
const SW_URL = '/firebase-messaging-sw.js';
const SYNC_INTERVAL_MS = 12 * 60 * 60 * 1000;
const DISMISS_INTERVAL_MS = 30 * 24 * 60 * 60 * 1000;
const DEFAULT_TITLE = 'つなげーと';

const hasWindow = () => typeof window !== 'undefined' && typeof document !== 'undefined';

let cachedConfig;
const readConfig = () => {
  if (cachedConfig !== undefined) return cachedConfig;
  cachedConfig = null;
  if (!hasWindow()) return cachedConfig;
  const el = document.querySelector(`meta[name="${META_NAME}"]`);
  if (!el) {
    // このモジュールは <head> の application パックから、csrf_meta_tags / web_push_meta_tag より前に評価される。
    // HTML の解析中（readyState=loading）に meta が見つからなくても「無い」と確定させず、次の呼び出しで探し直す
    if (document.readyState === 'loading') cachedConfig = undefined;
    return null;
  }
  try {
    cachedConfig = JSON.parse(el.content);
  } catch (e) {
    cachedConfig = null;
  }
  return cachedConfig;
};

const isSupported = () =>
  hasWindow() &&
  window.isSecureContext === true &&
  'Notification' in window &&
  'serviceWorker' in navigator &&
  'PushManager' in window;

const readJson = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};
const writeJson = (key, value) => {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    /* localStorage が使えない環境では購読状態を覚えられないだけ */
  }
};
const readState = () => (hasWindow() ? readJson(STORAGE_KEY) : null);
const writeState = (state) => writeJson(STORAGE_KEY, state);

// 1本の <script> を読む。失敗した要素は取り除き、次の呼び出しで作り直せるようにする
// （要素を残して再利用すると load/error は二度と発火せず、再試行が永遠に待つ）
const loadScript = (src) =>
  new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => {
      script.remove();
      reject(new Error(`failed to load ${src}`));
    });
    document.head.appendChild(script);
  });

// SDK の読み込みと初期化を1本の Promise にまとめる。失敗したら捨てて次回やり直す
let messagingPromise = null;
const getMessaging = () => {
  if (!messagingPromise) {
    messagingPromise = (async () => {
      const config = readConfig();
      await loadScript(`${SDK_BASE}${config.sdkVersion}/firebase-app-compat.js`);
      await loadScript(`${SDK_BASE}${config.sdkVersion}/firebase-messaging-compat.js`);
      const firebase = window.firebase;
      if (!firebase.apps.length) firebase.initializeApp(config.firebase);
      return firebase.messaging();
    })();
    messagingPromise.catch(() => {
      messagingPromise = null;
    });
  }
  return messagingPromise;
};

// SW 登録と SDK 読み込みは独立なので並べて待つ
const fetchToken = async () => {
  const [registration, messaging] = await Promise.all([navigator.serviceWorker.register(SW_URL), getMessaging()]);
  return messaging.getToken({ vapidKey: readConfig().vapidKey, serviceWorkerRegistration: registration });
};

// サーバへ登録。リダイレクト等で HTML が返ってきた場合（device_id が無い）は失敗として扱う
const saveToken = async (token) => {
  const previous = readState();
  const previousToken = previous && previous.token && previous.token !== token ? previous.token : undefined;
  const res = await axios.post('/api/web_push/subscribe', { token, previous_token: previousToken });
  if (!res.data || !res.data.device_id) throw new Error('subscribe did not return device_id');
  writeState({ userId: readConfig().userId, token, syncedAt: Date.now() });
};

const releaseToken = (token) => axios.post('/api/web_push/unsubscribe', { token });

const isEnabled = () => {
  const config = readConfig();
  const state = readState();
  return !!(
    config &&
    isSupported() &&
    Notification.permission === 'granted' &&
    state &&
    state.userId === config.userId &&
    state.token
  );
};

const status = () => {
  const supported = isSupported();
  return {
    available: !!readConfig(),
    supported,
    permission: supported ? Notification.permission : 'unsupported',
    enabled: isEnabled(),
  };
};

const listeners = new Set();
const emitChange = () => {
  const current = status();
  listeners.forEach((cb) => {
    try {
      cb(current);
    } catch (e) {
      /* 購読側の例外で他の購読者を巻き込まない */
    }
  });
};
const onChange = (cb) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};

// 通知が指す部屋をすでに開いているなら、手前での表示は出さない
const isViewingLink = (link) => {
  try {
    const url = new URL(link, window.location.origin);
    return url.origin === window.location.origin && url.pathname === window.location.pathname;
  } catch (e) {
    return false;
  }
};

let foregroundListening = false;
const listenForeground = async () => {
  if (foregroundListening) return;
  foregroundListening = true;
  try {
    const messaging = await getMessaging();
    messaging.onMessage(async (payload) => {
      const notification = payload.notification || {};
      const link = (payload.fcmOptions && payload.fcmOptions.link) || (payload.data && payload.data.link) || `${window.location.origin}/message`;
      if (isViewingLink(link)) return;

      const registration = await navigator.serviceWorker.getRegistration(SW_URL);
      if (!registration) return;
      await registration.showNotification(notification.title || DEFAULT_TITLE, {
        body: notification.body || '',
        icon: notification.icon,
        tag: link,
        data: { tgLink: link }, // クリック時の遷移は Service Worker 側（show.js.erb）が担う
      });
    });
  } catch (e) {
    foregroundListening = false;
  }
};

const subscribe = async () => {
  const config = readConfig();
  if (!config) throw new Error('web push is not available on this page');
  if (!isSupported()) throw new Error('web push is not supported by this browser');

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return;

    const token = await fetchToken();
    await saveToken(token);
    listenForeground();
  } finally {
    emitChange();
  }
};

const unsubscribe = async () => {
  const state = readState();
  try {
    if (state && state.token) {
      try {
        const messaging = await getMessaging();
        await messaging.deleteToken();
      } catch (e) {
        /* SDK 側の解除に失敗してもサーバ側の解除は進める（サーバが止めれば通知は来ない） */
      }
      await releaseToken(state.token);
    }
    writeState(null);
  } finally {
    emitChange();
  }
};

const shouldShowBanner = (current = status()) => {
  if (!current.available || !current.supported || current.enabled) return false;
  if (current.permission !== 'default') return false; // 拒否済みは案内しても許可できない
  const dismissedAt = readJson(DISMISS_KEY);
  return !(dismissedAt && Date.now() - dismissedAt < DISMISS_INTERVAL_MS);
};
const dismissBanner = () => {
  writeJson(DISMISS_KEY, Date.now());
  emitChange();
};

// ページの描画が終わって手が空いてから実行する（SDK の読み込みを React の mount と競合させない）
const whenIdle = (fn) => {
  const run = () => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(() => fn(), { timeout: 5000 });
    else window.setTimeout(fn, 1000);
  };
  if (document.readyState === 'complete') run();
  else window.addEventListener('load', run, { once: true });
};

// 購読済みのブラウザだけ: 必要ならトークンをサーバへ送り直し、手前表示のリスナーを張る
const autoSync = async () => {
  const config = readConfig();
  if (!config || !isSupported()) return;
  const state = readState();
  if (!state || !state.token) return;

  if (state.userId !== config.userId) {
    // 別のユーザーでログインしたブラウザ。前のユーザー宛の通知がこのブラウザに出続けないよう止める
    try {
      await releaseToken(state.token);
    } catch (e) {
      /* 次のページ表示で再試行する */
    }
    writeState(null);
    emitChange();
    return;
  }
  if (Notification.permission !== 'granted') {
    writeState(null); // ブラウザ側で取り消されていた
    emitChange();
    return;
  }

  listenForeground();
  const recentlySynced = state.syncedAt && Date.now() - state.syncedAt < SYNC_INTERVAL_MS;
  if (recentlySynced && config.deviceActive) return;
  try {
    const token = await fetchToken();
    await saveToken(token);
    emitChange();
  } catch (e) {
    /* 次のページ表示で再試行する */
  }
};

const api = { status, subscribe, unsubscribe, shouldShowBanner, dismissBanner, onChange };

if (hasWindow()) {
  window.TunaGateWebPush = api;
  // meta はこのパックより後ろにあるので、読み込み時には判定せず、描画が終わってから見る
  whenIdle(() => {
    if (readConfig()) autoSync();
  });
}

export default api;
